import {
  Badge,
  Box,
  Button,
  Checkbox,
  ColorSwatch,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconWindow } from "@tabler/icons-react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { sky_blue } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  TAG_API,
  TAG_COLORS,
  TAG_STATUS,
  TagObjectType,
  TblDmTag,
  TblDmTagCategory,
} from "../../../model/TblDmTag";

type TagFormValues = {
  categoryId: string | null;
  tagName: string;
  color: string;
  description: string;
  active: boolean;
};

const TagForm = ({ tag, categories, objectTypes, onSaved }: TagFormProps) => {
  const [visible, { open, close }] = useDisclosure(false);

  const form = useForm<TagFormValues>({
    validateInputOnChange: true,
    initialValues: {
      categoryId: tag?.categoryId ?? null,
      tagName: tag?.tagName ?? "",
      color: tag?.color ?? TAG_COLORS[0].value,
      description: tag?.description ?? "",
      active: tag ? tag.status !== TAG_STATUS.inactive : true,
    },
    validate: {
      categoryId: (value) => (value ? null : "Vui lòng chọn danh mục !"),
      tagName: (value) => {
        if (!value?.trim()) return "Vui lòng nhập tên tag !";
        if (value.trim().length > 255)
          return "Tên tag không được dài hơn 255 ký tự !";
        return null;
      },
    },
  });

  const categoryOptions = objectTypes
    .map((o) => ({
      group: o.name,
      items: categories
        .filter((c) => c.type === o.code)
        .map((c) => ({ value: c.id, label: c.categoryName })),
    }))
    .filter((g) => g.items.length > 0);

  const selectedCategory = categories.find(
    (c) => c.id === form.values.categoryId
  );
  const selectedObjectName = objectTypes.find(
    (o) => o.code === selectedCategory?.type
  )?.name;

  const handleSubmit = async (values: TagFormValues) => {
    open();
    const body = {
      id: tag?.id,
      categoryId: values.categoryId,
      tagName: values.tagName.trim(),
      color: values.color,
      description: values.description?.trim(),
      status: values.active ? TAG_STATUS.active : TAG_STATUS.inactive,
    };
    const res = tag
      ? await repositoryPos.put<MessageResponse<boolean>>(TAG_API.updateTag, body)
      : await repositoryPos.post<MessageResponse<string>>(TAG_API.createTag, body);
    close();
    if (res?.success) {
      NotificationExtension.Success(
        tag ? "Cập nhật thành công !" : "Thêm thành công !"
      );
      onSaved();
      modals.closeAll();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={form.onSubmit(handleSubmit)}
      w={{ base: "300px", md: "440px" }}
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Stack gap={14} mt={10}>
        <Box>
          <Select
            label="Danh mục"
            placeholder="Chọn danh mục tag"
            withAsterisk
            searchable
            data={categoryOptions}
            {...form.getInputProps("categoryId")}
          />
          {selectedObjectName ? (
            <Text size="xs" c="dark.4" mt={4}>
              Tag dùng cho đối tượng của danh mục:{" "}
              <Text span size="xs" fw={700} c="dark">
                {selectedObjectName}
              </Text>
            </Text>
          ) : null}
        </Box>

        <TextInput
          label="Tên tag"
          placeholder="Nhập tên tag"
          withAsterisk
          {...form.getInputProps("tagName")}
        />

        <Box>
          <Text size="sm" fw={500} id="tag-color-label">
            Màu hiển thị
          </Text>
          <Group
            gap={8}
            mt={6}
            role="radiogroup"
            aria-labelledby="tag-color-label"
          >
            {TAG_COLORS.map((c) => {
              const selected = form.values.color === c.value;
              return (
                <ColorSwatch
                  key={c.value}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={c.label}
                  color={`var(--mantine-color-${c.value}-6)`}
                  size={28}
                  withShadow={false}
                  style={{
                    cursor: "pointer",
                    border: "none",
                    boxShadow: selected
                      ? `0 0 0 2px #fff, 0 0 0 4px var(--mantine-color-${c.value}-6)`
                      : "none",
                  }}
                  onClick={() => form.setFieldValue("color", c.value)}
                />
              );
            })}
          </Group>
          <Group gap={8} mt={8}>
            <Text size="xs" c="dark.4">
              Xem trước
            </Text>
            <Badge
              variant="light"
              radius="xl"
              color={form.values.color}
              tt="none"
            >
              {form.values.tagName?.trim() || "[Tên tag]"}
            </Badge>
          </Group>
        </Box>

        <Textarea
          label="Mô tả"
          autosize
          minRows={2}
          {...form.getInputProps("description")}
        />

        <Checkbox
          label="Sử dụng"
          {...form.getInputProps("active", { type: "checkbox" })}
        />
      </Stack>

      <Group justify="end" mt="md">
        <Button
          type="button"
          color="gray"
          loading={visible}
          onClick={() => modals.closeAll()}
          leftSection={!visible ? <IconWindow size={18} /> : undefined}
        >
          Đóng
        </Button>
        <Button
          type="submit"
          color={sky_blue.base}
          loading={visible}
          leftSection={!visible ? <IconCheck size={18} /> : undefined}
        >
          Lưu
        </Button>
      </Group>
    </Box>
  );
};

export default TagForm;

type TagFormProps = {
  tag?: TblDmTag;
  categories: TblDmTagCategory[];
  objectTypes: TagObjectType[];
  onSaved: () => void;
};
